/**
 * Сообщение одной строкой: для ответа, закрепа, поиска и уведомлений.
 *
 * Раньше в каждом из этих мест было своё `text || "Вложение"`, и звонок,
 * опрос или «Иван закрепил сообщение» везде превращались в «Вложение».
 * Формулировки повторяют Telegram, а не придумываются на месте.
 */

import type { TelegramCallDto, TelegramMessageDto } from '@tw/shared-types';

const MEDIA_LABELS: Record<string, string> = {
  photo: 'Фотография',
  video: 'Видео',
  animation: 'GIF',
  document: 'Файл',
  voice: 'Голосовое сообщение',
  audio: 'Аудио',
  videoNote: 'Видеосообщение',
  sticker: 'Стикер',
  other: 'Вложение',
};

/** Название вида вложения; у стикера — его эмодзи, у файла — имя. */
export function mediaLabel(media: NonNullable<TelegramMessageDto['media']>): string {
  if (media.kind === 'sticker' && media.emoji) return `${media.emoji} Стикер`;
  if (media.kind === 'other' && media.label) return media.label;
  return media.fileName ?? MEDIA_LABELS[media.kind] ?? 'Вложение';
}

/** «Исходящий звонок», «Пропущенный видеозвонок» — как в Telegram. */
export function callLabel(call: TelegramCallDto, outgoing: boolean): string {
  const noun = call.video ? 'видеозвонок' : 'звонок';
  switch (call.outcome) {
    case 'missed':
      return `Пропущенный ${noun}`;
    case 'cancelled':
      return `Отменённый ${noun}`;
    case 'declined':
      return `Отклонённый ${noun}`;
    case 'disconnected':
      return `Прерванный ${noun}`;
    default:
      return outgoing ? `Исходящий ${noun}` : `Входящий ${noun}`;
  }
}

/** «5:32» — длительность разговора. */
export function formatCallDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Краткое описание сообщения.
 *
 * Порядок: текст → служебная отметка → звонок → вложение. Поля `service`,
 * `call` и `label` читаются защищённо — старый сервер их не присылает, и
 * тогда получается прежнее «Вложение», а не пустая строка.
 */
export function messageSummary(message: TelegramMessageDto): string {
  const text = message.text?.trim();
  if (text) return text;
  if (message.service) return message.service;
  if (message.call) return callLabel(message.call, message.direction === 'OUTBOUND');
  if (message.media) return mediaLabel(message.media);
  return message.hasMedia ? 'Вложение' : 'Сообщение';
}
